import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import SignatureCanvas from "react-signature-canvas";
import "./FormFields.css";

const DEFAULT_WIDTH = 460;
const DEFAULT_HEIGHT = 100;

const parseDimension = (value, fallback) => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return fallback;
    }

    if (trimmed.endsWith("%")) {
      return fallback;
    }

    const parsed = parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  return fallback;
};

const formatDimension = (value, fallback) => {
  if (typeof value === "number") {
    return `${value}px`;
  }

  if (typeof value === "string" && value.trim()) {
    return value;
  }

  return `${fallback}px`;
};

const SignatureField = forwardRef(
  (
    {
      label = "Signature",
      required = false,
      width = DEFAULT_WIDTH,
      height = DEFAULT_HEIGHT,
      onEnd,
      onClear,
      style = {},
      className = "",
      labelStyle = {},
      canvasStyle = {},
      ...props
    },
    ref
  ) => {
    const sigPadRef = useRef();
    const containerRef = useRef(null);
    const [canvasDimensions, setCanvasDimensions] = useState(() => ({
      width: parseDimension(width, DEFAULT_WIDTH),
      height: parseDimension(height, DEFAULT_HEIGHT),
    }));

    // Expose clear and isEmpty methods to parent
    useImperativeHandle(ref, () => ({
      clear: () => {
        sigPadRef.current?.clear();
      },
      isEmpty: () => {
        return sigPadRef.current?.isEmpty();
      },
      getCanvas: () => {
        return sigPadRef.current?.getCanvas();
      },
    }));

    const handleClear = () => {
      sigPadRef.current?.clear();
      if (onClear) {
        onClear();
      }
    };

    useEffect(() => {
      const updateDimensions = () => {
        const containerWidth =
          containerRef.current?.offsetWidth || DEFAULT_WIDTH;

        setCanvasDimensions({
          width: parseDimension(width, containerWidth) || containerWidth,
          height: parseDimension(height, DEFAULT_HEIGHT) || DEFAULT_HEIGHT,
        });
      };

      updateDimensions();

      let resizeObserver;
      if (
        typeof ResizeObserver !== "undefined" &&
        containerRef.current &&
        typeof width === "string" &&
        width.includes("%")
      ) {
        resizeObserver = new ResizeObserver(() => updateDimensions());
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }, [width, height]);

    return (
      <div
        ref={containerRef}
        className={`form-group ${className}`}
        style={{
          marginBottom: "8px",
          width: formatDimension(width, DEFAULT_WIDTH),
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "5px",
          }}
        >
          <label style={{ fontSize: "12px", ...labelStyle }}>
            {label}
            {required && (
              <span className="required" style={{ marginLeft: "-1px" }}>
                *
              </span>
            )}
          </label>

          <svg
            onClick={handleClear}
            style={{ cursor: "pointer" }}
            width="50"
            height="14"
            viewBox="0 0 50 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 14C5.16667 14 4.38611 13.8417 3.65833 13.525C2.93056 13.2083 2.29722 12.7806 1.75833 12.2417C1.21944 11.7028 0.791667 11.0694 0.475 10.3417C0.158333 9.61389 0 8.83333 0 8H1.33333C1.33333 9.3 1.78611 10.4028 2.69167 11.3083C3.59722 12.2139 4.7 12.6667 6 12.6667C7.3 12.6667 8.40278 12.2139 9.30833 11.3083C10.2139 10.4028 10.6667 9.3 10.6667 8C10.6667 6.7 10.2139 5.59722 9.30833 4.69167C8.40278 3.78611 7.3 3.33333 6 3.33333H5.9L6.93333 4.36667L6 5.33333L3.33333 2.66667L6 0L6.93333 0.966667L5.9 2H6C6.83333 2 7.61389 2.15833 8.34167 2.475C9.06944 2.79167 9.70278 3.21944 10.2417 3.75833C10.7806 4.29722 11.2083 4.93056 11.525 5.65833C11.8417 6.38611 12 7.16667 12 8C12 8.83333 11.8417 9.61389 11.525 10.3417C11.2083 11.0694 10.7806 11.7028 10.2417 12.2417C9.70278 12.7806 9.06944 13.2083 8.34167 13.525C7.61389 13.8417 6.83333 14 6 14Z"
              fill="#0D6EFD"
            />
            <path
              d="M24.1641 8.83496H25.4766C25.4082 9.46387 25.2282 10.0267 24.9365 10.5234C24.6449 11.0202 24.2324 11.4144 23.6992 11.7061C23.166 11.9932 22.5007 12.1367 21.7031 12.1367C21.1198 12.1367 20.5889 12.0273 20.1104 11.8086C19.6364 11.5898 19.2285 11.2799 18.8867 10.8789C18.5449 10.4733 18.2806 9.98796 18.0938 9.42285C17.9115 8.85319 17.8203 8.21973 17.8203 7.52246V6.53125C17.8203 5.83398 17.9115 5.2028 18.0938 4.6377C18.2806 4.06803 18.5472 3.5804 18.8936 3.1748C19.2445 2.76921 19.666 2.45703 20.1582 2.23828C20.6504 2.01953 21.2041 1.91016 21.8193 1.91016C22.5713 1.91016 23.207 2.05143 23.7266 2.33398C24.2461 2.61654 24.6494 3.00846 24.9365 3.50977C25.2282 4.00651 25.4082 4.58301 25.4766 5.23926H24.1641C24.1003 4.77441 23.9818 4.37565 23.8086 4.04297C23.6354 3.70573 23.3893 3.44596 23.0703 3.26367C22.7513 3.08138 22.3343 2.99023 21.8193 2.99023C21.3773 2.99023 20.9876 3.07454 20.6504 3.24316C20.3177 3.41178 20.0374 3.65104 19.8096 3.96094C19.5863 4.27083 19.4176 4.64225 19.3037 5.0752C19.1898 5.50814 19.1328 5.98893 19.1328 6.51758V7.52246C19.1328 8.01009 19.1829 8.4681 19.2832 8.89648C19.388 9.32487 19.5452 9.70085 19.7549 10.0244C19.9645 10.348 20.2311 10.6032 20.5547 10.79C20.8783 10.9723 21.2611 11.0635 21.7031 11.0635C22.2637 11.0635 22.7103 10.9746 23.043 10.7969C23.3757 10.6191 23.6263 10.3639 23.7949 10.0312C23.9681 9.69857 24.0911 9.2998 24.1641 8.83496ZM28.457 1.5V12H27.1855V1.5H28.457ZM33.5566 12.1367C33.0417 12.1367 32.5745 12.0501 32.1553 11.877C31.7406 11.6992 31.3828 11.4508 31.082 11.1318C30.7858 10.8128 30.5579 10.4346 30.3984 9.99707C30.2389 9.55957 30.1592 9.08105 30.1592 8.56152V8.27441C30.1592 7.67285 30.248 7.13737 30.4258 6.66797C30.6035 6.19401 30.8451 5.79297 31.1504 5.46484C31.4557 5.13672 31.8021 4.88835 32.1895 4.71973C32.5768 4.55111 32.9779 4.4668 33.3926 4.4668C33.9212 4.4668 34.377 4.55794 34.7598 4.74023C35.1471 4.92253 35.4639 5.17773 35.71 5.50586C35.9561 5.82943 36.1383 6.21224 36.2568 6.6543C36.3753 7.0918 36.4346 7.57031 36.4346 8.08984V8.65723H30.9111V7.625H35.1699V7.5293C35.1517 7.20117 35.0833 6.88216 34.9648 6.57227C34.8509 6.26237 34.6686 6.00716 34.418 5.80664C34.1673 5.60612 33.8255 5.50586 33.3926 5.50586C33.1055 5.50586 32.8411 5.56738 32.5996 5.69043C32.3581 5.80892 32.1507 5.98665 31.9775 6.22363C31.8044 6.46061 31.6699 6.75 31.5742 7.0918C31.4785 7.43359 31.4307 7.8278 31.4307 8.27441V8.56152C31.4307 8.91243 31.4785 9.24284 31.5742 9.55273C31.6745 9.85807 31.818 10.127 32.0049 10.3594C32.1963 10.5918 32.4264 10.7741 32.6953 10.9062C32.9688 11.0384 33.2786 11.1045 33.625 11.1045C34.0716 11.1045 34.4499 11.0133 34.7598 10.8311C35.0697 10.6488 35.3408 10.4049 35.5732 10.0996L36.3389 10.708C36.1794 10.9495 35.9766 11.1797 35.7305 11.3984C35.4844 11.6172 35.1813 11.7949 34.8213 11.9316C34.4658 12.0684 34.0443 12.1367 33.5566 12.1367ZM42.2725 10.7354V6.92773C42.2725 6.63607 42.2132 6.38314 42.0947 6.16895C41.9808 5.9502 41.8076 5.78158 41.5752 5.66309C41.3428 5.5446 41.0557 5.48535 40.7139 5.48535C40.3949 5.48535 40.1146 5.54004 39.873 5.64941C39.6361 5.75879 39.4492 5.90234 39.3125 6.08008C39.1803 6.25781 39.1143 6.44922 39.1143 6.6543H37.8496C37.8496 6.38997 37.918 6.12793 38.0547 5.86816C38.1914 5.6084 38.3874 5.3737 38.6426 5.16406C38.9023 4.94987 39.2122 4.78125 39.5723 4.6582C39.9368 4.5306 40.3424 4.4668 40.7891 4.4668C41.3268 4.4668 41.8008 4.55794 42.2109 4.74023C42.6257 4.92253 42.9492 5.19824 43.1816 5.56738C43.4186 5.93197 43.5371 6.38997 43.5371 6.94141V10.3867C43.5371 10.6328 43.5576 10.8949 43.5986 11.1729C43.6442 11.4508 43.7103 11.6901 43.7969 11.8906V12H42.4775C42.4137 11.8542 42.3636 11.6605 42.3271 11.4189C42.2907 11.1729 42.2725 10.945 42.2725 10.7354ZM42.4912 7.51562L42.5049 8.4043H41.2266C40.8665 8.4043 40.5452 8.43392 40.2627 8.49316C39.9801 8.54785 39.7432 8.63216 39.5518 8.74609C39.3604 8.86003 39.2145 9.00358 39.1143 9.17676C39.014 9.34538 38.9639 9.54362 38.9639 9.77148C38.9639 10.0039 39.0163 10.2158 39.1211 10.4072C39.2259 10.5986 39.3831 10.7513 39.5928 10.8652C39.807 10.9746 40.069 11.0293 40.3789 11.0293C40.7663 11.0293 41.1081 10.9473 41.4043 10.7832C41.7005 10.6191 41.9352 10.4186 42.1084 10.1816C42.2861 9.94466 42.3818 9.71452 42.3955 9.49121L42.9355 10.0996C42.9036 10.291 42.8171 10.5029 42.6758 10.7354C42.5345 10.9678 42.3454 11.1911 42.1084 11.4053C41.876 11.6149 41.598 11.7904 41.2744 11.9316C40.9554 12.0684 40.5954 12.1367 40.1943 12.1367C39.693 12.1367 39.2533 12.0387 38.875 11.8428C38.5013 11.6468 38.2096 11.3848 38 11.0566C37.7949 10.724 37.6924 10.3525 37.6924 9.94238C37.6924 9.5459 37.7699 9.19727 37.9248 8.89648C38.0798 8.59115 38.3031 8.33822 38.5947 8.1377C38.8864 7.93262 39.2373 7.77767 39.6475 7.67285C40.0576 7.56803 40.5156 7.51562 41.0215 7.51562H42.4912ZM46.791 5.76562V12H45.5264V4.60352H46.7568L46.791 5.76562ZM49.1016 4.5625L49.0947 5.73828C48.9899 5.71549 48.8896 5.70182 48.7939 5.69727C48.7028 5.68815 48.598 5.68359 48.4795 5.68359C48.1878 5.68359 47.9303 5.72917 47.707 5.82031C47.4837 5.91146 47.2946 6.03906 47.1396 6.20312C46.9847 6.36719 46.8617 6.56315 46.7705 6.79102C46.6839 7.01432 46.627 7.26042 46.5996 7.5293L46.2441 7.73438C46.2441 7.28776 46.2874 6.86849 46.374 6.47656C46.4652 6.08464 46.6042 5.73828 46.791 5.4375C46.9779 5.13216 47.2148 4.89518 47.502 4.72656C47.7936 4.55339 48.14 4.4668 48.541 4.4668C48.6322 4.4668 48.737 4.47819 48.8555 4.50098C48.974 4.51921 49.056 4.53971 49.1016 4.5625Z"
              fill="#0D6EFD"
            />
          </svg>
        </div>

        <div
          style={{
            border: "1px solid #CED4DA",
            width: "100%",
            borderRadius: "5px",
            height: formatDimension(height, DEFAULT_HEIGHT),
            ...canvasStyle,
          }}
        >
          <SignatureCanvas
            ref={sigPadRef}
            penColor="black"
            onEnd={onEnd}
            style={{ width: "100%", height: "100%" }}
            canvasProps={{
              width: canvasDimensions.width,
              height: canvasDimensions.height,
              className: "signature-canvas",
            }}
            {...props}
          />
        </div>
      </div>
    );
  }
);

SignatureField.displayName = "SignatureField";

export default SignatureField;
